import type * as React from "react";
import { useState } from "react";

import t from "./locales";
import Button from "../Shared/Button";
import type { IPrice } from "./types";
import { CURRENT_CART_VERSION } from "./constants";
import type { ICartItems, IOrderItem, IPeople, IResort } from "./types";
import { retrieveCartItems, storeCartItems } from "./helpers";
import BankeiLiftTicketModal from "./BankeiLiftTicketModal";
import { getImage } from "../utils/pathHelper";
import earlyBirdIcon from "../PriceCalculator/images/alarm-clock.svg";
import i18n from "../i18n";
import { format } from "date-fns";

// Fake API integration for new.
const syncCart = () => {
	return new Promise<void>((resolve) => {
		setTimeout(() => {
			resolve();
		}, 1000);
	});
};

const url = new URL(window.location.href);

export interface IProps {
	itemId: number;
	itemName: string;
	people: IPeople;
	price: IPrice;
	numberOfVouchers: number;
	resort?: IResort;
	maxParticipants?: number;
	validate: () => boolean;
	deposit: number;
	isCharge: boolean;
	seasonpass: boolean;
	forAxess: boolean;
	merchantId?: string;
	typeLift?: boolean;
	isEarlyBird?: boolean;
	saleDateTo?: Date;
	setProduct?: boolean;
	/** リクエスト商品など: カートを経由せず決済入力へ */
	directCheckout?: boolean;
}

const SnowItemCartButtonNoDate: React.FC<IProps> = (props) => {
	const {
		itemId,
		itemName,
		people,
		price,
		numberOfVouchers,
		resort,
		maxParticipants,
		validate,
		deposit,
		isCharge,
		forAxess,
		merchantId,
		typeLift,
		isEarlyBird,
		saleDateTo,
		setProduct,
		directCheckout,
	} = props;
	const [isLoading, toggleLoading] = useState(false);
	const [isModalShown, setIsModalShown] = useState(false);

	const shouldShowModal = merchantId === "m-bankei" && typeLift === true;

	const primaryButtonLabel = directCheckout ? t.proceedToReservation : t.addToCart;

	const goToPaymentNew = (): void => {
		if (isLoading) {
			return;
		}
		if (!validate()) {
			return;
		}
		toggleLoading(true);
		const { adultNum, childNum, studentNum, seniorNum } = people;
		const query = [
			`item_id=${itemId}`,
			`order[adult_num]=${adultNum}`,
			`order[child_num]=${childNum}`,
			`order[student_num]=${studentNum ?? 0}`,
			`order[senior_num]=${seniorNum ?? 0}`,
		];
		location.href = `/snow/me/payment/new?${query.join("&")}`;
	};

	const addToCart = async (): Promise<void> => {
		if (isLoading) {
			return;
		}
		if (!validate()) {
			return;
		}
		toggleLoading(true);
		const prevCartItems: ICartItems = retrieveCartItems(CURRENT_CART_VERSION);
		const orderItems: IOrderItem[] = prevCartItems.orderItems || [];
		// 特殊取り扱い(2023年11月)
		for (let i = 1; i <= numberOfVouchers; i += 1) {
			orderItems.push({
				itemId,
				itemName,
				people,
				price,
				maxParticipants,
				resort,
				type: "Order",
				deposit,
				isCharge,
				forAxess,
				setProduct,
			});
		}
		const cartItems = { ...prevCartItems, orderItems };
		await syncCart();
		storeCartItems(cartItems);
		toggleLoading(false);
	};

	const handleClick = async (event: React.MouseEvent): Promise<void> => {
		event.preventDefault();
		if (directCheckout) {
			if (shouldShowModal) {
				setIsModalShown(true);
			} else {
				goToPaymentNew();
			}
			return;
		}
		if (shouldShowModal) {
			setIsModalShown(true);
		} else {
			await addToCart();
		}
	};

	const handleModalConfirm = async (): Promise<void> => {
		if (directCheckout) {
			goToPaymentNew();
			return;
		}
		await addToCart();
	};

	return (
		<>
			{/* snow-cart-button = gtmのカートインイベント用のクラス */}
			{/* TODO: クラス以外の方法でトリガーする */}
			{isEarlyBird ? (
				<Button
					href="#"
					onClick={handleClick}
					isLoading={isLoading}
					snowItemButton={true}
					className="snow-cart-button snow-cart-button--early-bird"
				>
					<div className="snow-cart-button--early-bird-note">
						<img
							src={getImage(earlyBirdIcon)}
							alt="Early Bird"
							width={16}
							height={16}
						/>
						<div className="snow-cart-button--early-bird-text">
							{i18n.t("snow_items.show.early_bird_sale_until_html", {
								date: format(saleDateTo, "MM/dd"),
							})}
						</div>
					</div>
					<div className="d-flex align-items-center justify-content-center">
						{primaryButtonLabel()}
					</div>
				</Button>
			) : (
				<Button
					href="#"
					onClick={handleClick}
					isLoading={isLoading}
					snowItemButton={true}
					className="snow-cart-button"
				>
					{primaryButtonLabel()}
				</Button>
			)}
			{shouldShowModal && (
				<BankeiLiftTicketModal
					isShown={isModalShown}
					onClose={() => setIsModalShown(false)}
					onConfirm={handleModalConfirm}
				/>
			)}
		</>
	);
};

export default SnowItemCartButtonNoDate;
